/**
 * Serializable - interfaces and symbols for object serialization.
 */

import type { IDumper, ILoader } from './factories.js';

export const ISerializableObjectTag = Symbol('ISerializable');

/**
 * Sentinel value returned by `resolveArrayElement` to signal that a deserialized
 * array slot should be skipped (e.g. a stale prefab child that was removed from
 * the template between revisions).
 */
export const STALE_PREFAB_CHILD_ARRAY_SLOT = Symbol('STALE_PREFAB_CHILD_ARRAY_SLOT');

export interface ISerializableObject {
  /**
   * Marker to identify objects that implement ISerializable.
   */
  readonly [ISerializableObjectTag]: true;

  /**
   * Serializes the object.
   *
   * @param dumper - The dumper instance to use
   */
  serialize?(dumper: IDumper): void;

  /**
   * Informs the dumper if the object is transient, so it can be skipped during serialization.
   *
   * @returns True if the object is transient
   */
  isTransient?(): boolean;

  /**
   * Deserializes the object.
   *
   * @param loader - The loader instance to use
   */
  deserialize?(loader: ILoader): void;

  /**
   * Called after the object has been deserialized and all properties are set.
   * This is useful for initializing state that depends on other properties.
   */
  postLoad?(): void;
}
